import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

// Same-origin: nginx (prod) and the Vite dev proxy both route /api to the
// backend. Absolute http:// URLs would be blocked as mixed content over HTTPS.
const API_URL = window.location.origin;

const getAuthHeaders = () => ({
	Authorization: `Bearer ${localStorage.getItem("token")}`,
});

export const fetchSuites = createAsyncThunk(
	"suites/fetchAll",
	async ({ workspaceId, projectId }) => {
		const params = {};
		if (workspaceId) params.workspaceId = workspaceId;
		if (projectId) params.projectId = projectId;

		const response = await axios.get(`${API_URL}/api/suites`, {
			headers: getAuthHeaders(),
			params,
		});
		return response.data;
	},
);

export const createSuite = createAsyncThunk("suites/create", async (suite) => {
	const response = await axios.post(`${API_URL}/api/suites`, suite, {
		headers: getAuthHeaders(),
	});
	return response.data;
});

export const updateSuite = createAsyncThunk(
	"suites/update",
	async ({ id, suite }) => {
		const response = await axios.put(`${API_URL}/api/suites/${id}`, suite, {
			headers: getAuthHeaders(),
		});
		return response.data;
	},
);

export const deleteSuite = createAsyncThunk("suites/delete", async (id) => {
	await axios.delete(`${API_URL}/api/suites/${id}`, {
		headers: getAuthHeaders(),
	});
	return id;
});

export const fetchSuiteTests = createAsyncThunk(
	"suites/fetchTests",
	async (suiteId) => {
		const response = await axios.get(
			`${API_URL}/api/suites/${suiteId}/tests`,
			{
				headers: getAuthHeaders(),
			},
		);
		return response.data;
	},
);

export const previewDynamicTests = createAsyncThunk(
	"suites/previewTests",
	async (criteria) => {
		const response = await axios.post(
			`${API_URL}/api/suites/preview-tests`,
			criteria,
			{
				headers: getAuthHeaders(),
			},
		);
		return response.data;
	},
);

export const executeSuite = createAsyncThunk(
	"suites/execute",
	async (suiteId) => {
		const response = await axios.post(
			`${API_URL}/api/suites/${suiteId}/execute`,
			{},
			{
				headers: getAuthHeaders(),
			},
		);
		return response.data;
	},
);

export const archiveSuite = createAsyncThunk("suites/archive", async (id) => {
	const response = await axios.post(
		`${API_URL}/api/suites/${id}/archive`,
		{},
		{
			headers: getAuthHeaders(),
		},
	);
	return response.data;
});

// Schedule thunks
export const fetchSchedules = createAsyncThunk(
	"suites/fetchSchedules",
	async ({ suiteId, workspaceId = "default" } = {}) => {
		const params = suiteId ? { suiteId } : { workspaceId };
		const response = await axios.get(`${API_URL}/api/schedules`, {
			headers: getAuthHeaders(),
			params,
		});
		return response.data;
	},
);

export const createSchedule = createAsyncThunk(
	"suites/createSchedule",
	async (schedule) => {
		const response = await axios.post(
			`${API_URL}/api/schedules`,
			schedule,
			{ headers: getAuthHeaders() },
		);
		return response.data;
	},
);

export const updateSchedule = createAsyncThunk(
	"suites/updateSchedule",
	async ({ id, schedule }) => {
		const response = await axios.put(
			`${API_URL}/api/schedules/${id}`,
			schedule,
			{ headers: getAuthHeaders() },
		);
		return response.data;
	},
);

export const toggleSchedule = createAsyncThunk(
	"suites/toggleSchedule",
	async ({ id, enabled }) => {
		const response = await axios.patch(
			`${API_URL}/api/schedules/${id}/toggle`,
			{ enabled },
			{ headers: getAuthHeaders() },
		);
		return response.data;
	},
);

export const deleteSchedule = createAsyncThunk(
	"suites/deleteSchedule",
	async (id) => {
		await axios.delete(`${API_URL}/api/schedules/${id}`, {
			headers: getAuthHeaders(),
		});
		return id;
	},
);

const suiteSlice = createSlice({
	name: "suites",
	initialState: {
		suites: [],
		selectedSuite: null,
		suiteTests: [],
		schedules: [],
		schedulesLoading: false,
		loading: false,
		error: null,
	},
	reducers: {
		selectSuite: (state, action) => {
			state.selectedSuite = action.payload;
		},
		clearSuiteTests: (state) => {
			state.suiteTests = [];
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchSuites.pending, (state) => {
				state.loading = true;
			})
			.addCase(fetchSuites.fulfilled, (state, action) => {
				state.loading = false;
				state.suites = action.payload;
			})
			.addCase(fetchSuites.rejected, (state, action) => {
				state.loading = false;
				state.error = action.error.message;
			})
			.addCase(createSuite.fulfilled, (state, action) => {
				state.suites.push(action.payload);
			})
			.addCase(updateSuite.fulfilled, (state, action) => {
				const index = state.suites.findIndex(
					(s) => s.id === action.payload.id,
				);
				if (index !== -1) {
					state.suites[index] = action.payload;
				}
			})
			.addCase(deleteSuite.fulfilled, (state, action) => {
				state.suites = state.suites.filter(
					(s) => s.id !== action.payload,
				);
			})
			.addCase(archiveSuite.fulfilled, (state, action) => {
				const index = state.suites.findIndex(
					(s) => s.id === action.payload.id,
				);
				if (index !== -1) {
					state.suites[index] = action.payload;
				}
			})
			.addCase(fetchSuiteTests.fulfilled, (state, action) => {
				state.suiteTests = action.payload;
			})

			// Schedule cases
			.addCase(fetchSchedules.pending, (state) => {
				state.schedulesLoading = true;
			})
			.addCase(fetchSchedules.fulfilled, (state, action) => {
				state.schedulesLoading = false;
				state.schedules = action.payload;
			})
			.addCase(fetchSchedules.rejected, (state) => {
				state.schedulesLoading = false;
			})
			.addCase(createSchedule.fulfilled, (state, action) => {
				state.schedules.push(action.payload);
			})
			.addCase(updateSchedule.fulfilled, (state, action) => {
				const index = state.schedules.findIndex(
					(s) => s.id === action.payload.id,
				);
				if (index !== -1) {
					state.schedules[index] = action.payload;
				}
			})
			.addCase(toggleSchedule.fulfilled, (state, action) => {
				const index = state.schedules.findIndex(
					(s) => s.id === action.payload.id,
				);
				if (index !== -1) {
					state.schedules[index] = action.payload;
				}
			})
			.addCase(deleteSchedule.fulfilled, (state, action) => {
				state.schedules = state.schedules.filter(
					(s) => s.id !== action.payload,
				);
			});
	},
});

export const { selectSuite, clearSuiteTests } = suiteSlice.actions;
export default suiteSlice.reducer;
